import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import api from "@/lib/api";

// Public token landing page for the Issue 10 Phase 1 email flows. `action`
// selects which endpoint to hit. The raw token comes from the emailed link.
const CONFIG = {
  approve: {
    endpoint: "/auth/email-change/approve",
    title: "Approve email change",
    working: "Approving the email change…",
  },
  verify: {
    endpoint: "/auth/email-change/verify",
    title: "Verify your new email",
    working: "Verifying your new email…",
  },
  cancel: {
    endpoint: "/auth/email-change/cancel-link",
    title: "Cancel email change",
    working: "Cancelling the email change…",
  },
  "recovery-verify": {
    endpoint: "/auth/recovery-email/verify",
    title: "Verify recovery email",
    working: "Verifying your recovery email…",
  },
  "rec-new-verify": {
    endpoint: "/auth/email-recovery/verify",
    title: "Verify your new email",
    working: "Verifying your new email…",
  },
  "rec-confirm": {
    endpoint: "/auth/email-recovery/confirm",
    title: "Confirm account recovery",
    working: "Confirming account recovery…",
  },
  "rec-cancel": {
    endpoint: "/auth/email-recovery/cancel-link",
    title: "Cancel account recovery",
    working: "Cancelling account recovery…",
  },
};

export default function EmailAction({ action }) {
  const cfg = CONFIG[action] || CONFIG.verify;
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("working"); // working | success | error
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      if (!token) {
        setState("error");
        setMessage("This link is missing its token. Please use the link from your email.");
        return;
      }
      try {
        const { data } = await api.post(cfg.endpoint, { token });
        setState("success");
        if (action === "cancel" || action === "rec-cancel") {
          setMessage(action === "rec-cancel"
            ? "The account-recovery request has been cancelled and all sessions were signed out. Your login email is unchanged."
            : "The email change has been cancelled. Your login email is unchanged.");
        } else if (data?.completed) {
          setMessage("Your email address has been updated. For your security you've been signed out everywhere — please sign in again with your new email.");
        } else if (action === "approve") {
          setMessage("Thanks — you've approved the change. It completes once the new email is also verified.");
        } else if (action === "verify") {
          setMessage("Your new email is verified. It completes once the change is also approved from your current email.");
        } else if (action === "rec-new-verify") {
          setMessage("Your new email is verified. Recovery completes once your recovery contact also confirms.");
        } else if (action === "rec-confirm") {
          setMessage("Recovery confirmed from your recovery email. It completes once your new email is also verified.");
        } else {
          setMessage("Your recovery email is now verified.");
        }
      } catch (e) {
        setState("error");
        setMessage(e?.response?.data?.detail || "This link is invalid or has expired.");
      }
    })();
  }, [action, cfg.endpoint, token]);

  return (
    <AuthShell title={cfg.title} subtitle="">
      <div className="space-y-4 text-center" data-testid="email-action-result">
        {state === "working" && (
          <div className="flex flex-col items-center gap-3 py-4" data-testid="email-action-working">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">{cfg.working}</p>
          </div>
        )}
        {state === "success" && (
          <div className="flex flex-col items-center gap-3 py-2" data-testid="email-action-success">
            <CheckCircle2 className="h-10 w-10 text-teal-600" />
            <p className="text-sm text-foreground">{message}</p>
            <Link to="/login" className="mt-1 font-[560] text-primary hover:underline" data-testid="email-action-login-link">
              Go to login
            </Link>
          </div>
        )}
        {state === "error" && (
          <div className="flex flex-col items-center gap-3 py-2" data-testid="email-action-error">
            <XCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-foreground">{message}</p>
            <Link to="/dashboard/account" className="mt-1 font-[560] text-primary hover:underline">
              Back to account settings
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}
